import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Pause } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useHoldClient } from "@/hooks/useProjects";
import { fullDay, inlineDayLabel } from "@/components/record/recordFormat";

/** Put a client on hold: PUT /api/companies/:id/hold with the reason and revisit day. */
export function HoldClientDialog({ companyId, open, onOpenChange }: { companyId: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const queryClient = useQueryClient();
  const holdMutation = useHoldClient();
  const [holdReason, setHoldReason] = useState("");
  const [holdRevisitDate, setHoldRevisitDate] = useState("");

  const handleHold = () => {
    holdMutation.mutate(
      { companyId, holdReason, holdRevisitDate: holdRevisitDate || undefined },
      {
        onSuccess: () => {
          toast.success("Client placed on hold");
          onOpenChange(false);
          setHoldReason("");
          setHoldRevisitDate("");
          queryClient.invalidateQueries({ queryKey: ["company", companyId] });
        },
        onError: () => toast.error("Failed to update status"),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Put client on hold</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="hold-reason">Reason for hold</Label>
            <Textarea id="hold-reason" value={holdReason} onChange={(e) => setHoldReason(e.target.value)} placeholder="e.g., Owner not ready to sell, waiting on financial audit..." rows={3} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hold-revisit">Expected revisit date</Label>
            <Input id="hold-revisit" type="date" value={holdRevisitDate} onChange={(e) => setHoldRevisitDate(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleHold} disabled={holdMutation.isPending} className="bg-amber-500 text-white hover:bg-amber-600">
            {holdMutation.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Pause className="mr-1 h-4 w-4" />}
            Put on hold
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The amber line under the facts while a client is on hold. hold_revisit_date
 * holds a picked day at midnight UTC, so its date part is the day; new Date()
 * would show it a day early in Central.
 */
export function HoldBanner({ holdDate, reason, revisitOn, today, onReactivate, pending }: {
  holdDate: string | null; reason: string | null; revisitOn: string | null; today: string; onReactivate: () => void; pending: boolean;
}) {
  const revisit = revisitOn ? revisitOn.slice(0, 10) : null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 dark:border-amber-900 dark:bg-amber-950/40">
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="min-w-0 flex-1 text-sm">
        <span className="font-medium text-amber-800 dark:text-amber-300">{holdDate ? `On hold since ${fullDay(holdDate)}` : "On hold"}</span>
        {reason && <span className="text-amber-700 dark:text-amber-400"> · {reason}</span>}
        {revisit && <span className="text-amber-600 dark:text-amber-400"> · revisit {inlineDayLabel(revisit, today)}</span>}
      </div>
      <Button
        size="sm"
        variant="outline"
        className="border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
        onClick={onReactivate}
        disabled={pending}
      >
        Reactivate
      </Button>
    </div>
  );
}
